import { useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { buttonStyle, ICON_SLOT } from 'components/buttons/buttonStyle';
import { Search as SearchIcon, ViewWeekOutlined as ViewWeekOutlinedIcon } from 'components/icons';
import EmptyState from 'components/EmptyState';
import { Button } from 'components/ui/button';
import { Checkbox } from 'components/ui/checkbox';
import { InputGroup, InputGroupAddon, InputGroupInput } from 'components/ui/input-group';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import { Separator } from 'components/ui/separator';

import { t } from 'utils/getTranslations';

export type ColumnPickerOption = {
    id: string;
    label: string;
    /** `lockVisible` / `fixedPx` columns can't be hidden, so the menu leaves them out entirely — a
     *  row that can never be unchecked is only noise (the row-actions column was the case that
     *  prompted this). The owner still passes them in: the hidden-set reconciliation needs the full
     *  option list to tell a locked column apart from an unknown id. */
    locked: boolean;
};

type Props = {
    options: ColumnPickerOption[];
    hidden: string[];
    onToggle: (columnId: string) => void;
    onReset: () => void;
};

/**
 * Toolbar button opening the per-table column show/hide menu. Toggling keeps the menu open so
 * several columns can be flipped in one visit; "Reset columns" also clears the persisted width
 * overrides for the table (the escape hatch for both mechanisms).
 *
 * The search field and the reset action stay pinned while only the column list scrolls: a table
 * can offer many columns, so a capped, scrollable list keeps the reset action reachable without
 * scrolling to the very bottom. The search field takes the focus when the menu opens.
 */
const ColumnPickerButton = ({ options, hidden, onToggle, onReset }: Props) => {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const searchRef = useRef<HTMLInputElement | null>(null);

    const filteredOptions = useMemo(() => {
        const togglable = options.filter((option) => !option.locked);
        const needle = query.trim().toLowerCase();
        if (!needle) return togglable;
        return togglable.filter((option) => option.label.toLowerCase().includes(needle));
    }, [options, query]);

    return (
        <Popover
            // Modal, like a menu: an outside click only closes it, and the page does not scroll under it.
            modal
            open={open}
            onOpenChange={(next) => {
                setOpen(next);
                if (!next) setQuery('');
            }}
        >
            <PopoverTrigger render={<Button {...buttonStyle({ variant: 'outlined' })} />}>
                <span className={ICON_SLOT.medium}>
                    <ViewWeekOutlinedIcon />
                </span>
                <FormattedMessage id="table.columns" />
            </PopoverTrigger>
            <PopoverContent
                align="start"
                sideOffset={4}
                initialFocus={searchRef}
                // A dropdown, stacked with the others above page chrome; its backdrop covers the page,
                // app header included, so an outside click lands on nothing but the dismissal.
                positionerClassName="z-(--z-dropdown)"
                backdropClassName="fixed inset-0 z-(--z-dropdown)"
                // Bounded flex column so only the option list scrolls while the search field and
                // the reset action stay pinned, regardless of viewport height. The new design caps it to
                // the room the positioner measured on its side of the trigger, so it never opens past the
                // viewport's edge.
                className="new:max-h-[min(var(--available-height),calc(100dvh-96px))] flex max-h-[calc(100dvh-96px)] w-[300px] flex-col gap-0 p-0 py-2"
            >
                <div className="shrink-0 px-3 pt-1 pb-1">
                    <InputGroup className="bg-paper">
                        <InputGroupAddon align="inline-start">
                            <SearchIcon />
                        </InputGroupAddon>
                        <InputGroupInput
                            ref={searchRef}
                            placeholder={t('table.search-columns')}
                            aria-label={t('table.search-columns')}
                            value={query}
                            onChange={(event) => setQuery(event.target.value)}
                        />
                    </InputGroup>
                </div>
                <div className="max-h-[480px] min-h-0 flex-1 overflow-y-auto overscroll-contain">
                    {filteredOptions.length === 0 ? (
                        <EmptyState size="inline" titleId="table.no-columns-found" surface="column-picker" />
                    ) : (
                        filteredOptions.map((option) => (
                            <label
                                key={option.id}
                                className="hover:bg-muted flex min-w-0 cursor-pointer items-center gap-2 px-4 py-1.5 text-sm select-none"
                            >
                                <Checkbox
                                    variant="secondary"
                                    checked={!hidden.includes(option.id)}
                                    onCheckedChange={() => onToggle(option.id)}
                                />
                                <span className="truncate" title={option.label}>
                                    {option.label}
                                </span>
                            </label>
                        ))
                    )}
                </div>
                <Separator className="my-1 shrink-0" />
                <button
                    type="button"
                    className="hover:bg-muted shrink-0 cursor-pointer px-4 py-1.5 text-start text-sm"
                    onClick={() => {
                        onReset();
                        setOpen(false);
                        setQuery('');
                    }}
                >
                    <FormattedMessage id="table.reset-columns" />
                </button>
            </PopoverContent>
        </Popover>
    );
};

export default ColumnPickerButton;
